import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import { Controller, useForm } from "react-hook-form";
import { RoutesPaths } from "../Router/constant";
import { CUSTOM_SELECT_STYLES, PUSH_NOTIFICATION_MODULE, SELECT_BOX_THEME } from "../constants";
import { createChat } from "../services/api";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { addProfileToCampaignAction, getCampaignListAction } from "../actions/peopleai";
const safeDecode = (value) => {
    if (!value) return "";
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
};

const toArray = (value) => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
};

const cleanNamePart = (value) => {
    const namePart = String(value || "").trim();
    return ["undefined", "null"].includes(namePart.toLowerCase()) ? "" : namePart;
};

const cleanName = (value) => (
    String(value || "")
        .split(" ")
        .map(cleanNamePart)
        .filter(Boolean)
        .join(" ")
);

export default function ViewProfileModal({ show, onClose, profile, pageType = 1 }) {
    const [expanded, setExpanded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const { allUserList } = useSelector((state) => state.user);
    const { campaignList } = useSelector((state) => state.peopleai);
    const dispatch = useDispatch();
    const navigate = useNavigate()
    const [isCampaignSubmitting, setIsCampaignSubmitting] = useState(false);

    const {
        control: chatControl,
        handleSubmit: handleChatSubmit,
        reset: resetChatForm,
        formState: { errors: chatErrors }
    } = useForm();
    const {
        control: campaignControl,
        handleSubmit: handleCampaignSubmit,
        reset: resetCampaignForm,
        formState: { errors: campaignErrors }
    } = useForm();
    // ✅ NEVER return before hooks
    const [showAllSkills, setShowAllSkills] = useState(false);
    const name = [
        cleanNamePart(profile?.first_name),
        cleanNamePart(profile?.last_name)
    ].filter(Boolean).join(" ") || cleanName(profile?.name);

    const location = safeDecode(profile?.location);

    const targeting = safeDecode(profile?.targeting);

    const avatar = profile?.imageUrl;

    const contactInfo = profile?.contactInfo || {};

    const education = profile?.education || [];

    const experience = profile?.experience || [];

    const skills = profile?.skills || [];

    const about = profile?.about || "";

    const aboutPreview = useMemo(() => {
        if (expanded) return about;

        const max = 260;

        return about.length > max ? about.slice(0, max).trim() + "..." : about;
    }, [about, expanded]);

    const industries = useMemo(() => {
        const raw = profile?.person_industry;
        if (!raw) return [];
        if (Array.isArray(raw)) return raw.map((s) => s.trim()).filter(Boolean);
        if (typeof raw === "string") return raw.split(",").map((s) => s.trim()).filter(Boolean);
        return [];
    }, [profile?.person_industry]);

    const campaignOptions = useMemo(() => {
        return Array.isArray(campaignList) ? campaignList : [];
    }, [campaignList]);

    useEffect(() => {
        if (show && pageType === 2) {
            dispatch(getCampaignListAction(1, 1000, "", "launched"));
        }
    }, [dispatch, pageType, show]);

    useEffect(() => {
        if (!show) {
            resetChatForm({
                userDetail: null
            });
            resetCampaignForm({
                campaignDetail: null
            });
            setExpanded(false);
            setShowAllSkills(false);
            setImageError(false);
        }
    }, [resetCampaignForm, resetChatForm, show]);
    // ✅ return after hooks

    const handleChatResponse = async (data) => {

        const attributes = {
            module: PUSH_NOTIFICATION_MODULE.BOB_PROFIILE,
            data: profile,
            participants: [data?.userDetail?.id],
            message: `Profile suggestion ${name}`
        };
        const response = await createChat(attributes);
        navigate(RoutesPaths.MESSAGE_DETAIL +
            "?room=" +
            response._id
        );
        onClose();

    };

    const getProfileIds = (item) => {
        const candidateIds = [
            item?.profileIds,
            item?.profileId,
            item?.id,
            item?._id
        ]
            .flatMap((value) => Array.isArray(value) ? value : [value])
            .filter(Boolean);

        return candidateIds[0] || "";
    };

    const handleAddProfileToCampaign = async (data) => {
        const profileId = getProfileIds(profile);
        if (!profileId) {
            return;
        }

        setIsCampaignSubmitting(true);
        const response = await dispatch(addProfileToCampaignAction({
            profileId,
            campaignId: data?.campaignDetail?.id || data?.campaignDetail?._id
        }));

        setIsCampaignSubmitting(false);

        if (response?.status) {
            resetCampaignForm({
                campaignDetail: null
            });
            onClose();
        }
    };

    if (!profile) return null;
    const visibleSkills = showAllSkills ? skills : skills.slice(0, 10);
    return (
        <Modal
            show={show}
            onHide={onClose}
            centered
            size="xl"
            dialogClassName="vpDesktopModal"
        >
            <div className="vpTopBar">
                <button className="vpCloseBtn" onClick={onClose}>
                    ×
                </button>
            </div>
            <Modal.Body className="vpDesktopBody">

                <div className="vpContentWrap">
                    <div className="vpRight">

                        <div className="vpCard vpLeftCard">
                            <div className="vpHeader" />
                            <div className="vpAvatarWrap">
                                {avatar && !imageError ? (
                                    <img
                                        src={avatar}
                                        className="vpAvatar"
                                        onError={() => setImageError(true)}
                                    />
                                ) : (
                                    <div className="vpAvatarFallback">
                                        {name?.charAt(0)}
                                    </div>
                                )}
                            </div>

                            <h3 className="vpName">
                                {name}
                                {profile?.is_verified && (
                                    <span className="vpVerified">
                                        <svg
                                            width="16"
                                            height="17"
                                            viewBox="0 0 16 17"
                                            fill="none"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M5.34778 2.09697C5.85112 1.14739 6.84948 0.5 8 0.5C9.15052 0.5 10.1489 1.14739 10.6522 2.09697C11.6796 1.78143 12.8433 2.0296 13.6569 2.84315C14.4704 3.65669 14.7186 4.82041 14.403 5.84778C15.3526 6.35112 16 7.34948 16 8.5C16 9.65052 15.3526 10.6489 14.403 11.1522C14.7186 12.1796 14.4704 13.3433 13.6569 14.1569C12.8433 14.9704 11.6796 15.2186 10.6522 14.903C10.1489 15.8526 9.15052 16.5 8 16.5C6.84947 16.5 5.85112 15.8526 5.34778 14.903C4.32041 15.2186 3.15669 14.9704 2.34314 14.1569C1.52961 13.3433 1.28144 12.1796 1.59698 11.1522C0.647392 10.6489 0 9.65052 0 8.5C0 7.34948 0.647391 6.35112 1.59697 5.84778C1.28143 4.82041 1.5296 3.65669 2.34314 2.84314C3.15669 2.0296 4.32041 1.78143 5.34778 2.09697ZM11.81 6.49246C11.8686 6.55104 11.8686 6.64602 11.81 6.70459L7.07236 11.4422C7.01378 11.5008 6.91881 11.5008 6.86023 11.4422L4.24393 8.82591C4.18536 8.76734 4.18536 8.67236 4.24393 8.61378L5.09246 7.76525C5.15104 7.70668 5.24602 7.70668 5.30459 7.76525L6.86023 9.32089C6.91881 9.37947 7.01378 9.37947 7.07236 9.32089L10.7493 5.64393C10.8079 5.58536 10.9029 5.58536 10.9614 5.64393L11.81 6.49246Z"
                                                fill={'#2978b5'}
                                            />
                                            <path
                                                d="M11.81 6.70459C11.8686 6.64602 11.8686 6.55104 11.81 6.49246L10.9614 5.64393C10.9029 5.58536 10.8079 5.58536 10.7493 5.64393L7.07236 9.32089C7.01378 9.37947 6.91881 9.37947 6.86023 9.32089L5.30459 7.76525C5.24602 7.70668 5.15104 7.70668 5.09246 7.76525L4.24393 8.61378C4.18536 8.67236 4.18536 8.76734 4.24393 8.82591L6.86023 11.4422C6.91881 11.5008 7.01378 11.5008 7.07236 11.4422L11.81 6.70459Z"
                                                fill="white"
                                            />
                                        </svg>
                                    </span>
                                )}
                            </h3>

                            <div className="vpLocation">
                                {location}
                            </div>

                        </div>
                        {(toArray(contactInfo?.phone).length > 0 ||
                            toArray(contactInfo?.email).length > 0 ||
                            toArray(contactInfo?.website).length > 0) && (
                                <div className="vpCard">
                                    <h5>Contact info</h5>

                                    {toArray(contactInfo?.phone).map((p, i) => (
                                        <div key={i}>
                                            {p?.areaCode?.dial_code}
                                            {p?.number}
                                        </div>
                                    ))}

                                    {toArray(contactInfo?.email).map((e, i) => (
                                        <div key={i}>{e?.email || e}</div>
                                    ))}
                                    {toArray(contactInfo?.website).map((e, i) => (
                                        <div key={i}><a className="websiteLink" href={e?.url || e} target="_blank" rel="noopener noreferrer">{e?.url || e}</a></div>
                                    ))}
                                </div>
                            )}
                        {education.length > 0 && (
                            <div className="vpCard">
                                <h5>Education</h5>

                                {education.map((item, i) => (
                                    <div key={i} className="vpItem">
                                        <div className="vpItemTitle">{item.school}</div>

                                        <div className="vpItemSub">{item.dates}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {experience.length > 0 && (
                            <div className="vpCard">
                                <h5>Experience</h5>

                                {experience.map((item, i) => (
                                    <div key={i} className="vpItem">
                                        <div className="vpItemTitle">{item.company}</div>

                                        <div>{item.title}</div>

                                        <div className="vpItemSub">{item.duration}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {skills.length > 0 && (
                            <div className="vpCard">
                                <h5>Skills</h5>

                                <div className="vpSkillHolder">
                                    {visibleSkills.map((skill, i) => (
                                        <span key={i} className="vpSkill">
                                            {skill}
                                        </span>
                                    ))}
                                </div>
                                {!showAllSkills && skills.length > 10 && (
                                    <span
                                        className="vpMoreText"
                                        onClick={() => setShowAllSkills(true)}
                                    >
                                        +{skills.length - 10} more
                                    </span>
                                )}
                                {showAllSkills && skills.length > 10 && (
                                    <div
                                        className="vpMoreText vpLessText"
                                        onClick={() => setShowAllSkills(false)}
                                    >
                                        Show less
                                    </div>
                                )}
                            </div>
                        )}
                        {industries.length > 0 && (
                            <div className="vpCard">
                                <h5>Industry</h5>

                                <div className="vpSkillHolder">
                                    {industries.map((industry, i) => (
                                        <span key={i} className="vpSkill">
                                            {industry}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {about && (
                            <div className="vpCard">
                                <h5>About</h5>

                                <p className="vpItemSub">{aboutPreview}</p>

                                {about.length > 260 && (
                                    <button
                                        className="vpReadMore"
                                        onClick={() => setExpanded(!expanded)}
                                    >
                                        {expanded ? "Show Less" : "Read More"}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {pageType === 2 &&
                        <div className="vpLeft">
                            <div className="vpCard">
                                <h5>Send Profile</h5>
                                <form onSubmit={handleChatSubmit(handleChatResponse)}>
                                    <div className="vpFormGroup">
                                        <label className="vpLabel">
                                            Select user
                                        </label>
                                        <Controller
                                            name="userDetail"
                                            control={chatControl}
                                            rules={{ required: true }}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    options={allUserList}
                                                    theme={SELECT_BOX_THEME}
                                                    styles={CUSTOM_SELECT_STYLES}
                                                    getOptionLabel={(option) =>
                                                        `${option.first_name} ${option.last_name} (${option.email})`
                                                    }
                                                    getOptionValue={(option) => option.email}
                                                    placeholder="Select user to send profile"
                                                />
                                            )}
                                        />
                                        {chatErrors.userDetail && (
                                            <div className="vpError">
                                                Please select user
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        type="submit"
                                        className="vpSendBtn"
                                    >
                                        Send Profile
                                    </button>
                                </form>
                            </div>
                            <div className="vpCard">
                                <h5>Add Profile To Campaign</h5>
                                <form onSubmit={handleCampaignSubmit(handleAddProfileToCampaign)}>
                                    <div className="vpFormGroup">
                                        <label className="vpLabel">
                                            Select campaign
                                        </label>
                                        <Controller
                                            name="campaignDetail"
                                            control={campaignControl}
                                            rules={{ required: true }}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    options={campaignOptions}
                                                    theme={SELECT_BOX_THEME}
                                                    styles={CUSTOM_SELECT_STYLES}
                                                    getOptionLabel={(option) => option?.name + (option?.creator ? ` (${option?.creator?.first_name} ${option?.creator?.last_name})` : "asd") || "-"}
                                                    getOptionValue={(option) => option?.id || option?._id}
                                                    placeholder="Select campaign"
                                                />
                                            )}
                                        />
                                        {campaignErrors.campaignDetail && (
                                            <div className="vpError">
                                                Please select campaign
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        type="submit"
                                        className="vpSendBtn"
                                        disabled={isCampaignSubmitting}
                                    >
                                        {isCampaignSubmitting ? "Sending..." : "Send To Campaign"}
                                    </button>
                                </form>
                            </div>
                        </div>
                    }
                </div>
            </Modal.Body>
        </Modal>
    );
}
